import { useCallback, useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import * as XLSX from 'xlsx';
import { createUniver, defaultTheme, LocaleType, mergeLocales } from '@univerjs/presets';
import { UniverSheetsCorePreset } from '@univerjs/preset-sheets-core';
import UniverPresetSheetsCoreEnUS from '@univerjs/preset-sheets-core/locales/en-US';
import '@univerjs/preset-sheets-core/lib/index.css';
import { UniverSheetsDataValidationPreset } from '@univerjs/preset-sheets-data-validation';
import UniverPresetSheetsDataValidationEnUS from '@univerjs/preset-sheets-data-validation/locales/en-US';
import '@univerjs/preset-sheets-data-validation/lib/index.css';
import { UniverSheetsFilterPreset } from '@univerjs/preset-sheets-filter';
import UniverPresetSheetsFilterEnUS from '@univerjs/preset-sheets-filter/locales/en-US';
import '@univerjs/preset-sheets-filter/lib/index.css';
import { api } from '../../api/client';
import { useNotify } from '../../components/notifications';
import { useSaveFlow, showDialog } from '../../components/confirm';
import { useHotkeys, formatCombo, type HotkeyDef } from '../../components/hotkeys';
import { StepHint } from '../../components/StepHint/StepHint';
import { TopbarStepper } from '../../components/StepIsland/TopbarStepper';
import { type IslandStep } from '../../components/StepIsland/StepIsland';
import { useEddResultStore, type EddResult } from './eddResultStore';
import { useUnsavedGuard } from '../../hooks/useUnsavedGuard';

// Accurate text width (px) via a shared canvas — matches Univer's 11px font so
// auto-fit columns actually fit the header/cell text.
const _mCanvas = typeof document !== 'undefined' ? document.createElement('canvas') : null;
const _mCtx = _mCanvas ? _mCanvas.getContext('2d') : null;
function textWidth(text: string): number {
  if (!_mCtx) return text.length * 7.5;
  _mCtx.font = '11px Arial, sans-serif';
  return _mCtx.measureText(text).width;
}

/** Univer workbook data from the generated sheets — one worksheet per tab, in
 *  the order the API returned them (EDDQueryBuilder.QueryName ascending). */
function buildWorkbookData(result: EddResult) {
  const sheetOrder: string[] = [];
  const sheets: Record<string, unknown> = {};
  result.sheets.forEach((s, i) => {
    const id = `edd-sheet-${i}`;
    sheetOrder.push(id);
    const cellData: Record<number, Record<number, { v: string | number }>> = {};
    // Column 0 is the "Select" checkbox column; the real columns shift to 1..n.
    // A1 (0,0) is the "select all" checkbox (0 = unchecked).
    const header: Record<number, { v: string | number }> = { 0: { v: 0 } };
    s.columns.forEach((c, ci) => { header[ci + 1] = { v: c }; });
    cellData[0] = header;
    s.rows.forEach((row, ri) => {
      const r: Record<number, { v: string | number }> = { 0: { v: 0 } };   // unchecked
      row.forEach((cell, ci) => { r[ci + 1] = { v: cell ?? '' }; });
      cellData[ri + 1] = r;
    });
    // Auto-size each column to its content (sample rows for perf).  Column 0 is a
    // fixed narrow checkbox column; freeze header + Select + first data column.
    //
    // The HEADER cell is wider than its text: the Filter preset overlays a
    // dropdown arrow on it.  Sizing to the text alone clipped every long caption
    // — "B_FILE_NAME" (66px) got a 88px column and rendered "B_FILE_NA▾", and
    // "B_LABORATORY_CERTIFYING_AGENCY" (213px) got 235px and clipped too (user
    // report 2026-07-15, who then resized by hand).  So the header claims its
    // text PLUS the arrow; data cells have no arrow and claim just their text.
    const HEADER_ARROW = 24;   // filter dropdown glyph + its gap
    const CELL_PAD     = 16;   // left/right cell padding
    const MIN_W = 64, MAX_W = 600;   // MAX raised 460 → 600: a 38-char PWS GUID
                                     // measures 234px and long captions ~213px,
                                     // both of which the old cap could clip.
    const sample = s.rows.slice(0, 300);
    const columnData: Record<number, { w: number }> = { 0: { w: 44 } };
    s.columns.forEach((c, ci) => {
      let max = textWidth(c ?? '') + HEADER_ARROW;  // header text + its filter arrow
      for (const r of sample) {
        const w = textWidth(String(r[ci] ?? ''));   // data cells (sampled, no arrow)
        if (w > max) max = w;
      }
      columnData[ci + 1] = { w: Math.min(Math.max(Math.ceil(max) + CELL_PAD, MIN_W), MAX_W) };
    });
    sheets[id] = {
      id,
      name: s.sheetName || `Sheet${i + 1}`,
      cellData,
      columnData,
      // Freeze the header row + the Select column + the first data column.
      freeze: { startRow: 1, startColumn: 2, xSplit: 2, ySplit: 1 },
      rowCount: Math.max(s.rows.length + 10, 50),
      columnCount: Math.max(s.columns.length + 4, 20),
    };
  });
  return { id: 'edd-workbook', name: result.templateName || 'EDD', sheetOrder, sheets };
}

// The three EDD templates that export as CMDP XML (server-built) instead of the
// generic client-side XML.  Must match IEddXmlGenerator.CmdpTemplateNames.
const CMDP_TEMPLATES = new Set(['CMDP_Microbial', 'CMDP_Chem/Radionuclides', 'CMDP_Op_LCRWQP']);

/** Separate Spreadsheet View opened by the EDD Report Query popup's OK — a tabbed
 *  Univer workbook (one tab per sheet).  Export downloads the persisted .xlsx. */
export default function EddSpreadsheetPage() {
  const navigate = useNavigate();
  const notify = useNotify();
  const runSave = useSaveFlow();
  const result = useEddResultStore((s) => s.result);
  const setResult = useEddResultStore((s) => s.setResult);
  // A generated EDD is only a PREVIEW until Save persists it ("Not saved" in the
  // header) — leaving now throws it away, so ask first ("Unsaved changes will be
  // lost. Do you want to proceed?"). There are no typed fields for the automatic
  // tracker to see on this page, so it reports its own state. Clean once saved.
  useUnsavedGuard(!!result && !result.saved);
  const [saving, setSaving] = useState(false);
  const [exportFmt, setExportFmt] = useState('');   // chosen format in the Export combo
  const [searchText, setSearchText] = useState('');
  const containerRef = useRef<HTMLDivElement | null>(null);
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const univerApiRef = useRef<any>(null);

  useEffect(() => {
    if (!result || !containerRef.current) return;
    // Mount Univer into a FRESH element owned by THIS effect run, never the shared
    // container. React dev mode (StrictMode) runs mount -> cleanup -> mount back to
    // back, and Univer tears down its own React root; disposing it synchronously
    // inside React's cleanup phase raced the second mount and left the sheet blank
    // ("Attempted to synchronously unmount a root while React was already
    // rendering", then "The column width is less than 0"). Production, which mounts
    // once, was never affected - so this only ever showed on the dev server.
    const host = document.createElement('div');
    host.style.cssText = 'position:absolute;inset:0';
    containerRef.current.appendChild(host);
    const { univer, univerAPI } = createUniver({
      locale: LocaleType.EN_US,
      locales: { [LocaleType.EN_US]: mergeLocales(UniverPresetSheetsCoreEnUS, UniverPresetSheetsDataValidationEnUS, UniverPresetSheetsFilterEnUS) },
      theme: defaultTheme,
      presets: [
        // ribbonType 'simple' drops the Start / Formulas / Data TAB STRIP (Univer
        // renders that ClassicMenu only for 'classic') and shows a single toolbar
        // row — user request 2026-07-15: "no need formula and data ... start tools
        // only".  The DataValidation + Filter presets stay loaded: the checkbox
        // column in A depends on data validation.
        UniverSheetsCorePreset({ container: host, ribbonType: 'simple' }),
        UniverSheetsDataValidationPreset(),
        UniverSheetsFilterPreset(),
      ],
    });
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    univerAPI.createWorkbook(buildWorkbookData(result) as any);
    univerApiRef.current = univerAPI;
    // Turn column 0 into CHECKBOXES on every sheet — including the HEADER cell A1,
    // which acts as the "select all" toggle for that sheet.
    try {
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      const fSheets: any[] = univerAPI.getActiveWorkbook()?.getSheets?.() ?? [];
      fSheets.forEach((fws, si) => {
        const n = result.sheets[si]?.rows.length ?? 0;
        if (n <= 0) return;
        const rule = univerAPI.newDataValidation().requireCheckbox().build();
        fws.getRange(0, 0, n + 1, 1).setDataValidation(rule);   // header A1 + data rows
      });
    } catch { /* best-effort — if the checkbox can't attach, exports simply find none */ }

    // Enable an Excel-style AutoFilter (funnel dropdown per column) over the header
    // row + all data rows, on every sheet.  Columns 1..N only — column 0 is the
    // Select checkbox column, which stays outside the filter.  Users can then click
    // any column's funnel to filter the rows by that column's values.
    try {
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      const fSheets: any[] = univerAPI.getActiveWorkbook()?.getSheets?.() ?? [];
      fSheets.forEach((fws, si) => {
        const sheet = result.sheets[si];
        const n = sheet?.rows.length ?? 0;
        const cols = sheet?.columns.length ?? 0;
        if (n <= 0 || cols <= 0) return;
        // header row 0 + n data rows, starting at the first real data column (1).
        fws.getRange(0, 1, n + 1, cols).createFilter?.();
      });
    } catch { /* filter is best-effort — grid still renders without it */ }

    // Ticking A1 (the header "select all") checks/clears the whole column on that
    // sheet.  Detect via the SheetValueChanged event's effectedRanges (guarded so
    // the cascade write doesn't re-trigger).
    let syncing = false;
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const coversA1 = (r: any) => {
      try {
        const rg = typeof r?.getRange === 'function' ? r.getRange() : null;
        if (rg && typeof rg.startRow === 'number') {
          return rg.startRow <= 0 && rg.endRow >= 0 && rg.startColumn <= 0 && rg.endColumn >= 0;
        }
        const row = typeof r?.getRow === 'function' ? r.getRow() : -1;
        const col = typeof r?.getColumn === 'function' ? r.getColumn() : -1;
        return row === 0 && col === 0;
      } catch { return false; }
    };
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    let disposable: any = null;
    try {
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      disposable = univerAPI.addEvent(univerAPI.Event.SheetValueChanged, (params: any) => {
        if (syncing) return;
        const ranges = params?.effectedRanges ?? [];
        if (!ranges.some(coversA1)) return;
        try {
          syncing = true;
          const fws = univerAPI.getActiveWorkbook()?.getActiveSheet();
          const name = fws?.getSheetName?.();
          const si = result.sheets.findIndex((s) => (s.sheetName || '') === name);
          const n = result.sheets[si >= 0 ? si : 0]?.rows.length ?? 0;
          const a1 = fws?.getRange(0, 0, 1, 1)?.getValue?.();
          const checked = a1 === 1 || a1 === true || a1 === '1';
          if (n > 0 && fws) fws.getRange(1, 0, n, 1).setValue(checked ? 1 : 0);
        } finally { syncing = false; }
      });
    } catch { /* event API best-effort */ }

    return () => {
      disposable?.dispose?.();
      univerApiRef.current = null;
      // Deferred so Univer's own root unmount never runs inside React's commit phase.
      setTimeout(() => {
        try { univer.dispose(); } catch { /* already torn down */ }
        host.remove();
      }, 0);
    };
  }, [result]);


  // ── Search Text: hide every data row that doesn't contain the typed text in
  // ANY column (case-insensitive), across all sheets.  A live VIEW filter only —
  // it doesn't change selection, the saved data, or exports; clearing the box
  // reveals every row again.  Hidden rows are coalesced into contiguous ranges
  // so filtering stays fast on large sheets.
  const applySearch = useCallback((raw: string) => {
    const q = raw.trim().toLowerCase();
    try {
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      const fSheets: any[] = univerApiRef.current?.getActiveWorkbook?.()?.getSheets?.() ?? [];
      fSheets.forEach((fws, si) => {
        const rows = result?.sheets[si]?.rows ?? [];
        const n = rows.length;
        if (n <= 0) return;
        fws.showRows(1, n);                 // reset — reveal every data row first
        if (!q) return;
        let runStart = -1;                  // start of the current hidden run (Univer row index)
        const flush = (endExclusive: number) => {
          if (runStart >= 0) { fws.hideRows(runStart, endExclusive - runStart); runStart = -1; }
        };
        for (let i = 0; i < n; i++) {
          const match = (rows[i] ?? []).some((c) => String(c ?? '').toLowerCase().includes(q));
          const uRow = i + 1;               // row 0 is the header
          if (match) flush(uRow); else if (runStart < 0) runStart = uRow;
        }
        flush(n + 1);
      });
    } catch { /* row-hide is best-effort */ }
  }, [result]);

  // Reset the search whenever a new EDD is generated (the workbook is rebuilt).
  useEffect(() => { setSearchText(''); }, [result]);
  // Re-apply the filter as the text changes (runs after the workbook is built).
  useEffect(() => { applySearch(searchText); }, [searchText, applySearch]);

  // ── Exports (client-side only; NO save — a record is created ONLY when the
  // user clicks Save).  File name = <base>_<timestamp>.<ext> — the SAME format as
  // the CMDP XML export (base = the EDD ID once saved, else the template name).
  const baseName = () => (result?.eddReportId || result?.templateName || 'EDD');
  const stamp = () => {
    const t = new Date();
    const p2 = (n: number) => String(n).padStart(2, '0');
    return `${t.getFullYear()}${p2(t.getMonth() + 1)}${p2(t.getDate())}${p2(t.getHours())}${p2(t.getMinutes())}${p2(t.getSeconds())}`;
  };
  const exportName = (ext: string) => `${baseName().replace(/[\\/:*?"<>|]/g, '_')}_${stamp()}.${ext}`;

  // Shared milestone-success MODAL for every "Export to" format (BO UI Standard
  // §13) via the global showDialog — the SAME centred acknowledgement as the EDD
  // Save + Download flows, not a transient banner (user request 2026-07-08).
  const exportedDialog = (format: string, fileName: string, count?: number) => {
    const rows = [{ label: 'Format', value: format }];
    if (count !== undefined) rows.push({ label: 'Rows', value: String(count) });
    rows.push({ label: 'File', value: fileName });
    return showDialog({
      type: 'success',
      title: 'EDD Exported',
      ...(result?.eddReportId ? { idLabel: 'EDD Report ID', idValue: result.eddReportId } : {}),
      rows,
      note: `Exported to ${format} successfully.`,
    });
  };
  const safeSheet = (name: string, i: number, used: Set<string>) => {
    let n = (name || `Sheet${i + 1}`).replace(/[\\/?*[\]:]/g, ' ').slice(0, 31) || `Sheet${i + 1}`;
    while (used.has(n)) n = `${n.slice(0, 27)}_${i}`.slice(0, 31);
    used.add(n);
    return n;
  };
  // The tab the user is currently viewing (for the single-sheet exports).
  const activeSheetIndex = () => {
    try {
      const name = univerApiRef.current?.getActiveWorkbook?.()?.getActiveSheet?.()?.getSheetName?.();
      if (name && result) {
        const i = result.sheets.findIndex((s) => (s.sheetName || '') === name);
        if (i >= 0) return i;
      }
    } catch { /* fall through */ }
    return 0;
  };
  const download = (filename: string, content: string, mime: string) => {
    const blob = new Blob([content], { type: mime });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url; a.download = filename;
    document.body.appendChild(a); a.click(); a.remove();
    setTimeout(() => URL.revokeObjectURL(url), 60_000);
  };

  // Checked data-row indices (column-0 checkbox = 1) for a given sheet.
  const checkedIndices = (si: number): Set<number> => {
    const set = new Set<number>();
    try {
      const s = result?.sheets[si]; if (!s) return set;
      const n = s.rows.length;
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      const fws: any = univerApiRef.current?.getActiveWorkbook?.()?.getSheets?.()?.[si];
      const values = fws?.getRange?.(1, 0, n, 1)?.getValues?.() ?? [];
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      values.forEach((row: any, i: number) => {
        const v = Array.isArray(row) ? row[0] : row;
        if (v === 1 || v === true || v === '1' || String(v).toLowerCase() === 'true') set.add(i);
      });
    } catch { /* ignore */ }
    return set;
  };
  // The active sheet's checked rows, or null (+ a validation warning) if none.
  const activeCheckedRows = () => {
    const s = result ? result.sheets[activeSheetIndex()] : undefined;
    if (!s) return null;
    const idx = checkedIndices(activeSheetIndex());
    if (idx.size === 0) {
      notify.warning('No records selected — tick one or more checkboxes in the “✓” column (or use Select all), then export.');
      return null;
    }
    return { sheet: s, rows: s.rows.filter((_, i) => idx.has(i)) };
  };
  // All 5 exports output ONLY the CHECKED rows (validation warns if none).
  const exportExcelAll = () => {
    if (!result) return;
    const perSheet = result.sheets.map((s, si) => ({ s, rows: s.rows.filter((_, i) => checkedIndices(si).has(i)) }));
    if (perSheet.every((p) => p.rows.length === 0)) {
      notify.warning('No records selected — tick some checkboxes (or use Select all), then export.');
      return;
    }
    const wb = XLSX.utils.book_new();
    const used = new Set<string>();
    perSheet.forEach(({ s, rows }, si) => {
      const ws = XLSX.utils.aoa_to_sheet([s.columns, ...rows.map((r) => r.map((c) => c ?? ''))]);
      XLSX.utils.book_append_sheet(wb, ws, safeSheet(s.sheetName, si, used));
    });
    const fn = exportName('xlsx');
    XLSX.writeFile(wb, fn);
    const total = perSheet.reduce((n, p) => n + p.rows.length, 0);
    void exportedDialog('Excel (all sheets)', fn, total);
  };
  const toExcel = () => {
    const pick = activeCheckedRows(); if (!pick) return;
    const wb = XLSX.utils.book_new();
    const ws = XLSX.utils.aoa_to_sheet([pick.sheet.columns, ...pick.rows.map((r) => r.map((c) => c ?? ''))]);
    XLSX.utils.book_append_sheet(wb, ws, safeSheet(pick.sheet.sheetName, 0, new Set()));
    const fn = exportName('xlsx');
    XLSX.writeFile(wb, fn);
    void exportedDialog('Excel', fn, pick.rows.length);
  };
  const toCsv = () => {
    const pick = activeCheckedRows(); if (!pick) return;
    const esc = (v: unknown) => { const t = String(v ?? ''); return /[",\n\r]/.test(t) ? `"${t.replace(/"/g, '""')}"` : t; };
    const lines = [pick.sheet.columns.map(esc).join(','), ...pick.rows.map((r) => r.map(esc).join(','))];
    const fn = exportName('csv');
    download(fn, lines.join('\r\n'), 'text/csv;charset=utf-8');
    void exportedDialog('CSV', fn, pick.rows.length);
  };
  const toTxt = () => {
    const pick = activeCheckedRows(); if (!pick) return;
    const lines = [pick.sheet.columns.join('\t'), ...pick.rows.map((r) => r.map((c) => String(c ?? '').replace(/\t/g, ' ')).join('\t'))];
    const fn = exportName('txt');
    download(fn, lines.join('\r\n'), 'text/plain;charset=utf-8');
    void exportedDialog('TXT', fn, pick.rows.length);
  };
  const toXml = () => {
    const pick = activeCheckedRows(); if (!pick) return;
    const escXml = (v: unknown) => String(v ?? '').replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;');
    const tag = (c: string) => (c || 'Col').replace(/[^A-Za-z0-9_]/g, '_').replace(/^(\d)/, '_$1');
    const rowsXml = pick.rows.map((r) =>
      `  <Row>\n${pick.sheet.columns.map((c, ci) => `    <${tag(c)}>${escXml(r[ci])}</${tag(c)}>`).join('\n')}\n  </Row>`).join('\n');
    const fn = exportName('xml');
    download(fn, `<?xml version="1.0" encoding="UTF-8"?>\n<EDD>\n${rowsXml}\n</EDD>`, 'application/xml');
    void exportedDialog('XML', fn, pick.rows.length);
  };

  // ── CMDP XML export (server-built) — for the 3 CMDP templates only.  Honors
  // the row selection like the other exports: it collects the ticked rows' sample
  // codes (the "sampleCd" column) and asks the server to build the CMDP XML for
  // ONLY those samples.  If nothing is ticked it shows the same "select rows"
  // warning; server-side validations surface via the shared notification.
  const isCmdp = !!result && CMDP_TEMPLATES.has(result.templateName ?? '');
  const selectedSampleCds = (): string[] => {
    const out = new Set<string>();
    result?.sheets.forEach((s, si) => {
      const ki = s.columns.findIndex((c) => (c ?? '').toLowerCase() === 'samplecd');
      if (ki < 0) return;
      checkedIndices(si).forEach((i) => {
        const v = s.rows[i]?.[ki];
        if (v !== null && v !== undefined && String(v).length > 0) out.add(String(v));
      });
    });
    return Array.from(out);
  };
  // ROW-level keys for the ticked rows — `sampleCdanalyteCdmethodCd`
  // (must match EddXmlGenerator.RowKeySep) so the XML exports ONLY the ticked
  // result rows, not the whole sample (user request 2026-07-13).
  const selectedRowKeys = (): string[] => {
    const SEP = '';
    const out = new Set<string>();
    result?.sheets.forEach((s, si) => {
      const col = (name: string) => s.columns.findIndex((c) => (c ?? '').toLowerCase() === name);
      const kSample = col('samplecd'); const kAnalyte = col('analytecd'); const kMethod = col('methodcd');
      if (kSample < 0 || kAnalyte < 0) return;
      checkedIndices(si).forEach((i) => {
        const row = s.rows[i]; if (!row) return;
        const sc = String(row[kSample] ?? '').trim();
        const an = String(row[kAnalyte] ?? '').trim();
        const me = kMethod >= 0 ? String(row[kMethod] ?? '').trim() : '';
        if (sc) out.add(`${sc}${SEP}${an}${SEP}${me}`);
      });
    });
    return Array.from(out);
  };
  const toCmdpXml = async () => {
    if (!result) return;
    const sampleCds = selectedSampleCds();
    const rowKeys = selectedRowKeys();
    if (sampleCds.length === 0) {
      notify.warning('No records selected — tick some checkboxes in the “✓” column (or use Select all), then export.');
      return;
    }
    try {
      const res = await api.post('/api/v1/edd/xml', {
        templateId: result.templateId, filter: result.filter, sampleCds, rowKeys,
      });
      const d = res.data?.data as { fileName: string; base64: string };
      const bin = atob(d.base64);
      const bytes = new Uint8Array(bin.length);
      for (let i = 0; i < bin.length; i++) bytes[i] = bin.charCodeAt(i);
      const blob = new Blob([bytes], { type: 'application/xml' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      const fn = `${(d.fileName || 'CMDP').replace(/[\\/:*?"<>|]/g, '_')}_${stamp()}.xml`;
      a.href = url; a.download = fn;
      document.body.appendChild(a); a.click(); a.remove();
      setTimeout(() => URL.revokeObjectURL(url), 60_000);
      void exportedDialog('CMDP XML', fn, sampleCds.length);
    } catch (e) {
      const msg = (e as { response?: { data?: { error?: { message?: string } } } })
        .response?.data?.error?.message ?? 'Failed to export the XML.';
      notify.error(msg);
    }
  };

  const EXPORTS: [string, () => void][] = [
    ['Export to Excel All Sheet', exportExcelAll],
    ['Export to Excel Single Sheet', toExcel],
    ['Export to CSV', toCsv],
    ['Export to TXT', toTxt],
    // CMDP templates emit the regulatory XML; everything else the generic XML.
    ['Export to XML', isCmdp ? toCmdpXml : toXml],
  ];

  // Run the format chosen in the Export combo — ONLY that one file downloads.
  const runExport = () => {
    const entry = EXPORTS.find(([label]) => label === exportFmt);
    if (!entry) { notify.warning('Choose a format in the list, then click Export.'); return; }
    entry[1]();
  };

  // Save = persist ONE EDDReportGenerator row (nothing was written on OK/preview).
  // Goes through the shared useSaveFlow (standing rule 7): "Do you want to Save?"
  // confirm → the existing /edd/save handler (race-safe EddReportID, best-effort
  // audit) → shared "Saved successfully." acknowledgement.  This is a Create, so
  // it is reason-exempt (§14a C3), and we deliberately DON'T navigate away
  // (`onSaved` omitted) — the user stays on the Spreadsheet View to keep
  // exporting the sheets, with the minted EddReportID shown in the header badge.
  const handleSave = async () => {
    if (!result || result.saved) return;
    setSaving(true);
    // Captured inside `save` so the milestone-success dialog (§13) can show the
    // minted EDD ID + detail rows after the record is created.
    let savedId = '';
    try {
      await runSave({
        save: async () => {
          const res = await api.post('/api/v1/edd/save', { templateId: result.templateId, filter: result.filter });
          const data = res.data?.data as { eddReportId: string; eddReportOid: string };
          savedId = data.eddReportId;
          setResult({ ...result, eddReportId: data.eddReportId, eddReportOid: data.eddReportOid, saved: true });
        },
        // §13 milestone-success MODAL (a new EDD record + ID was created) — green,
        // ID + detail rows, replacing the plain "Saved successfully." banner.
        successDialog: () => ({
          type: 'success',
          title: 'EDD Report Saved',
          idLabel: 'EDD Report ID',
          idValue: savedId,
          rows: [
            { label: 'Template', value: result.templateName || '—' },
            { label: 'Sheets', value: String(result.sheets.length) },
          ],
          note: 'You can export the sheets or return to the EDD Report Generator list.',
        }),
        onError: (e) => {
          const msg = (e as { response?: { data?: { error?: { message?: string } } } })
            .response?.data?.error?.message ?? 'Save failed.';
          notify.error(msg);
        },
      });
    } finally {
      setSaving(false);
    }
  };

  // ── Phase 2.3 · Keyboard shortcuts for the EDD Spreadsheet action bar
  //    (CLAUDE.md standing rule 14, §18.6, ADR-0013).  Each key MIRRORS a
  //    header-bar button — same handler, same disabled state — grouped under the
  //    form title.  Registered UNCONDITIONALLY (before the early return); the
  //    array is gated on `result` since the buttons live in the main view only,
  //    and Save is dropped once saved (the button becomes a "✔ Saved" chip).
  //    Esc/Back is deliberately NOT bound — the Univer spreadsheet owns Esc.
  const eddKeys: HotkeyDef[] = [];
  if (result) {
    const grp = 'EDD Spreadsheet';
    if (!result.saved) {
      eddKeys.push({ combo: 'mod+s', label: 'Save', group: grp, order: 1, disabled: saving,
        run: () => { void handleSave(); } });
    }
    eddKeys.push({ combo: 'alt+x', label: 'Export', group: grp, order: 2, disabled: !exportFmt,
      run: () => runExport() });
  }
  useHotkeys(eddKeys);

  if (!result) {
    return (
      <div style={{ padding: 24 }}>
        <p>No EDD has been generated in this session.</p>
        <button className="ca-btn" onClick={() => navigate('/m/eddgen')}>← Back to EDD Report Generator</button>
      </div>
    );
  }

  // Dynamic Island stepper (BO UI Standard §5) — the Spreadsheet View is steps
  // 2–4 of the EDD flow: Query (done) → Load (done, sheets shown) → Select Format
  // (optional export) → Save (active until saved).
  const eddSteps: IslandStep[] = [
    { id: 'query',  label: 'Query',         state: 'done' },
    { id: 'load',   label: 'Load',          state: 'done' },
    // Select Format goes GREEN once a format is chosen in the Export combo;
    // 'optional' if the user skips straight to Save.
    { id: 'format', label: 'Select Format', state: exportFmt ? 'done' : (result.saved ? 'optional' : 'active') },
    { id: 'save',   label: 'Save',          state: result.saved ? 'done' : (exportFmt ? 'active' : 'todo') },
  ];

  return (
    <div style={{ display: 'flex', flexDirection: 'column', height: '100%', minHeight: 0 }}>
      <TopbarStepper steps={eddSteps} />
      <StepHint tone={result.saved ? 'complete' : 'guidance'}>
        {result.saved
          ? <><b>Done · EDD {result.eddReportId} saved.</b> Use <b>Export to</b> to download any format, or <b>← Back</b> to the list.</>
          : <><b>Step 3 · Select a format &amp; finish.</b> Tick the <b>✓</b> rows, use <b>Export to</b> to download, or click <b>Save</b> to store this EDD.</>}
      </StepHint>
      <div style={{ display: 'flex', alignItems: 'center', gap: 12, padding: '8px 12px', borderBottom: '1px solid var(--ai-line)' }}>
        <button className="ca-btn" onClick={() => navigate('/m/eddgen')}>← Back</button>
        <strong style={{ fontSize: 15 }}>{result.templateName}</strong>
        <span style={{ color: 'var(--ai-muted)', fontSize: 13 }}>
          {result.eddReportId ? `${result.eddReportId} · ` : ''}
          {result.saved ? 'Saved · ' : 'Not saved · '}
          {result.sheets.length} sheet{result.sheets.length === 1 ? '' : 's'}
        </span>
        <span style={{ color: 'var(--ai-muted)', fontSize: 12 }}>
          Tick the ✓ column (A1 = select all) to choose rows
        </span>
        {/* Search Text — live, case-insensitive filter across ALL columns.
            Uses the SHARED BO-standard search box (.list-search, theme/boStandard.css)
            so it matches every other form's search exactly — same white pill, radius,
            border, 12.5px text, grey glyph + × clear (user request 2026-07-15).
            Only the width is set here; everything else is inherited. */}
        <div className="list-search" style={{ width: 220 }}>
          <span className="tb-i">🔍</span>
          <input
            type="text"
            value={searchText}
            onChange={(e) => setSearchText(e.target.value)}
            placeholder="Search…"
            aria-label="Search Text"
          />
          {searchText && (
            <span className="list-search-clear" title="Clear search"
              onClick={() => setSearchText('')}>×</span>
          )}
        </div>
        <div style={{ marginLeft: 'auto', display: 'flex', gap: 8, alignItems: 'center' }}>
          {result.saved ? (
            <span style={{ color: 'var(--ai-pill-ok-fg)', fontSize: 13, fontWeight: 600 }}>
              ✔ Saved {result.eddReportId}
            </span>
          ) : (
            <button className="ca-btn ca-btn-solid" onClick={handleSave} disabled={saving}
              title={`Save (${formatCombo('mod+s')})`}>
              <i className="ti ti-device-floppy" /> {saving ? 'Saving…' : 'Save'}
            </button>
          )}
          {/* Choose a format in the combo, then click Export — only that file
              downloads (user request 2026-07-09). */}
          <select
            value={exportFmt}
            onChange={(e) => setExportFmt(e.target.value)}
            aria-label="Export format"
            style={{
              font: 'inherit', fontSize: 13, padding: '5px 8px', minWidth: 190,
              border: '1px solid var(--ai-line)', borderRadius: 4,
              background: '#fff', color: 'var(--ai-ink, #1f2430)', cursor: 'pointer',
            }}>
            <option value="">Export to… (choose a format)</option>
            {EXPORTS.map(([label]) => <option key={label} value={label}>{label}</option>)}
          </select>
          <button className="ca-btn ca-btn-solid" onClick={runExport} disabled={!exportFmt}
            title={(exportFmt ? `Export ${exportFmt}` : 'Choose a format first') + ` (${formatCombo('alt+x')})`}>
            <i className="ti ti-download" /> Export
          </button>
        </div>
      </div>
      <div ref={containerRef} style={{ flex: 1, minHeight: 0, width: '100%', position: 'relative' }} />
    </div>
  );
}
